import {
  Box,
  CountSticker,
  Icon,
  Text,
  type IconName,
  type Omittable,
} from "@salestrak/ui";
import * as React from "react";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useInboxUnreadCount } from "#app/hooks/inbox/use-inbox-unread-count";
import { useModuleRail } from "./module-rail-context";
import { MoreModulesTile } from "./more-modules-tile";
import { useModuleTilePress } from "./use-module-tile-press";

/** Minimum width of one strip tile — captions get room to breathe and short
 * ones ("AI", "Map") still land on a substantial block. */
const STRIP_TILE_MIN_WIDTH = 68;

/**
 * One strip tile: a small CARD, not the rail's bare chip — inside the drawer
 * the tiles ARE the tier-1 surface, and they have to read against the drawer
 * fill in both modes: in light the card's fill + drop lift them off the
 * `sidebar` gray, in dark its hairline draws them. Activation is a themed
 * ring with the glyph in brand ink and the caption rising to full ink —
 * never an inverted slab, which would out-shout the menu below it. Home's
 * unread count is the rail's sticker at the card's top-right corner.
 *
 * `...rest` forwards to the root Box so a tile can anchor an overlay — the
 * More tile passes this as a `DrawerTrigger render`.
 */
export function StripTile({
  icon,
  selectedIcon,
  label,
  badge,
  badgeDot = false,
  active = false,
  ...rest
}: Omittable<{
  icon: IconName;
  selectedIcon?: IconName;
  label: string;
  badge?: string;
  badgeDot?: boolean;
  active?: boolean;
}> &
  Omit<React.ComponentProps<typeof Box<"button">>, "as" | "children">) {
  return (
    <Box<"button">
      align="center"
      aria-label={label}
      as="button"
      border
      className={
        active
          ? "inset-ring-[1.5px] inset-ring-(--brand) text-sidebar-foreground"
          : "text-muted-foreground"
      }
      direction="column"
      gap="xs"
      hoverBackground={!active}
      justify="center"
      minWidth={STRIP_TILE_MIN_WIDTH}
      paddingX="sm"
      paddingY="base"
      position="relative"
      rounded="lg"
      shrink={false}
      surface="card"
      {...rest}
    >
      <Box className={active ? "text-(--brand)" : undefined}>
        <Icon
          name={active && selectedIcon != null ? selectedIcon : icon}
          size="md"
        />
      </Box>
      <Text size="2xs" truncate weight="medium">
        {label}
      </Text>
      {badge != null && (
        // The sticker rides the CARD's corner, where a badge sits on a
        // captioned app icon — the glyph stays whole. The card keeps its fill
        // in every state (activation is a ring), so the sticker never inverts.
        <CountSticker count={badge} dot={badgeDot} />
      )}
    </Box>
  );
}

/**
 * ModuleStrip — the module rail turned horizontal, for the COMPACT drawer.
 *
 * Inside the drawer a vertical rail column would spend its width restating
 * what one row can say, so tier 1 becomes a strip of filled tiles across the
 * top of the navigation surface: the user's shown tiles in their configured
 * order, closed by the More tile (hidden tiles, upcoming modules, Invite and
 * Customize live in its popup, exactly as on the rail). Captions always
 * render — the strip exists on touch screens where a tooltip cannot explain
 * an unlabeled glyph.
 */
/** Edge-fade masks, keyed by which edges have clipped tiles beyond them. A
 * fade says "more this way"; a hard cut reads as the last tile being cropped.
 * Only a scrolled-past edge fades — at rest the first tile renders whole. */
const STRIP_MASKS = {
  both: "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]",
  left: "[mask-image:linear-gradient(to_right,transparent,black_24px)]",
  right: "[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]",
  none: undefined,
} as const;

export function ModuleStrip() {
  const { activeTileId, browseTileId, countDot, railTiles } = useModuleRail();
  // Browsing highlights the menu being SHOWN, not the route — the strip and
  // the pane below it must always agree on which module they present.
  const shownTileId = browseTileId ?? activeTileId;
  const handleTilePress = useModuleTilePress();
  const inboxUnreadCount = useInboxUnreadCount();
  const canInvite = useCanInvite();
  const [inviteOpen, setInviteOpen] = React.useState(false);

  const scrollRef = React.useRef<HTMLElement>(null);
  const [clipped, setClipped] = React.useState<keyof typeof STRIP_MASKS>("none");
  const updateClipped = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setClipped(left && right ? "both" : left ? "left" : right ? "right" : "none");
  }, []);
  // Initial measure — the drawer is a fixed 86dvw, so scroll is the only
  // thing that moves the edges after mount.
  React.useEffect(updateClipped, [updateClipped, railTiles]);

  const shownTiles = railTiles.filter((tile) => tile.show);
  const overflowTiles = railTiles.filter((tile) => !tile.show);

  return (
    <Box
      align="start"
      // The drawer's tier-1 navigation — named like the rail it stands in for.
      aria-label="Modules"
      as="nav"
      className={STRIP_MASKS[clipped]}
      direction="row"
      gap="sm"
      onScroll={updateClipped}
      overflow="auto"
      paddingX="base"
      paddingY="sm"
      ref={scrollRef}
      // The edge fades say "more this way"; a painted bar under the row
      // would say it twice and steal a rung from the pane below.
      scrollbar="hidden"
    >
      {shownTiles.map((tile, index) => {
        // Same badge rule as the rail: Home carries the live inbox count,
        // except while Home is active — the module menu right below carries
        // it on its own Inbox row there.
        const badge =
          tile.id === "home" && shownTileId !== "home" && inboxUnreadCount > 0
            ? String(inboxUnreadCount)
            : tile.badge;
        return (
          <StripTile
            active={tile.id === shownTileId}
            badge={badge}
            badgeDot={countDot}
            icon={tile.icon}
            key={`${tile.id}-${index}`}
            label={tile.label}
            onClick={(event) => {
              // A browsed tile must not sit half-clipped under the edge fade
              // while its menu fills the pane below.
              event.currentTarget.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest",
              });
              handleTilePress(tile);
            }}
            selectedIcon={tile.selectedIcon}
          />
        );
      })}
      <MoreModulesTile
        onInvitePress={
          // The strip is the compact tier's whole rail, so Invite rides its
          // More popup — there is no rail foot to pin it to.
          canInvite
            ? () => setInviteOpen(true)
            : undefined
        }
        onTilePress={handleTilePress}
        overflowTiles={overflowTiles}
        trigger={<StripTile icon="grid-dots" label="More" />}
      />
      {canInvite && (
        <InviteMembersDialog onOpenChange={setInviteOpen} open={inviteOpen} />
      )}
    </Box>
  );
}
